/**
 * Human Resources · one person's file.
 *
 * Everything HR holds about somebody on one page: who they are and their
 * photo, their contract and (for those allowed) their pay, their attendance
 * day by day, leave and sick leave with what is left, the dates that matter
 * to them, reviews and discipline (confidential), the letters they asked for
 * and HR issued, and their documents. Every figure opens the record it came
 * from; every action is one button away.
 */
import { useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  useHrClockEvents,
  useHrDecide,
  useHrFile,
  useHrNotifications,
  useHrRules,
  useHrSetAttendance,
  useHrSetPhoto,
} from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, EmptyState, Segmented, Skeleton, Table } from "../../design/components";
import { grossPay, mergeRules, STATUS_LABEL, type HrEmployee, type HrRules } from "../../domain/hr";
import {
  ATTENDANCE,
  ATTENDANCE_BY,
  attendanceSummary,
  DATE_LABEL,
  datesOf,
  LEAVE_BY,
  leaveDays,
  leaveType,
  LETTER_BY,
  letterText,
  sickLeave,
  vacationOf,
  type AttendanceStatus,
  type HrRecord,
  type HrStaffFile,
  type RecordKind,
} from "../../domain/hrFile";
import { METHOD, REQUEST_BY } from "../../domain/hrClock";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { useHrAccess } from "./access";
import { EmployeeDialog } from "./EmployeeDialog";
import {
  Avatar,
  Chip,
  downloadHtml,
  escapeHtml,
  h,
  niceDate,
  PreviewBar,
  printHtml,
  shrinkPhoto,
  StatusChip,
  todayIso,
  useShowHourly,
} from "./kit";
import { ClockPolicy } from "./ClockPolicy";
import { MessageList } from "./Messages";
import { RecordDialog } from "./RecordDialog";
import { HrError, rate, s, sar, SUITE, tradeLabel } from "./shared";

type Tab =
  "overview" | "attendance" | "leave" | "dates" | "reviews" | "discipline" | "letters" | "documents" | "messages";

export function StaffFilePage() {
  const { id } = useParams();
  const session = useSession();
  const [params, setParams] = useSearchParams();
  const q = useHrFile(id);
  const rulesQ = useHrRules();
  const access = useHrAccess();
  const [dialog, setDialog] = useState<RecordKind | "edit" | null>(null);
  const [letter, setLetter] = useState<HrRecord | null>(null);
  const f = q.data;
  const rules = rulesQ.data?.rules ?? mergeRules(null);
  const can = {
    pay: access.can.pay && (f?.can.pay ?? true),
    confidential: access.can.confidential && (f?.can.confidential ?? true),
    write: access.can.write && (f?.can.write ?? true),
    approve: access.can.approve && (f?.can.approve ?? true),
  };
  const tabs: { value: Tab; label: string }[] = [
    { value: "overview", label: "Overview" },
    { value: "attendance", label: "Attendance" },
    { value: "leave", label: "Leave" },
    { value: "dates", label: "Dates" },
    ...(can.confidential
      ? [
          { value: "reviews" as Tab, label: "Performance" },
          { value: "discipline" as Tab, label: "Discipline" },
        ]
      : []),
    { value: "letters", label: "Letters & requests" },
    { value: "documents", label: "Documents & notes" },
    { value: "messages", label: "Messages" },
  ];
  const wanted = (params.get("tab") as Tab) || "overview";
  const tab = tabs.some((x) => x.value === wanted) ? wanted : "overview";
  const setTab = (v: Tab) => setParams(v === "overview" ? {} : { tab: v }, { replace: true });

  const records = useMemo(
    () => (f?.records ?? []).filter((r) => can.confidential || !r.confidential),
    [f, can.confidential],
  );

  useScreenContext(
    f
      ? {
          route: `/hr-people/${f.employee.id}`,
          title: f.employee.name,
          summary: [
            t("{name}, {title} in {dept}, {y} years' service.", {
              name: f.employee.name,
              title: f.employee.jobTitle ?? "—",
              dept: f.employee.department ?? "—",
              y: f.employee.serviceYears.toFixed(1),
            }),
          ],
          offers: [
            {
              label: t("What leave does this person have left?"),
              ask: `how much annual leave does ${f.employee.name} have left?`,
            },
          ],
          worksInFull: true,
        }
      : null,
  );

  return (
    <Page
      eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })}
      title={f?.employee.name ?? t("Staff file")}
    >
      <PreviewBar />
      <p className={s.crumbs}>
        <Link to="/hr">{t("Human Resources")}</Link> › <Link to="/hr-people">{t("Workforce")}</Link> ›{" "}
        {f?.employee.name ?? "…"}
      </p>
      {q.isLoading && <Skeleton rows={8} />}
      {q.error && <HrError error={q.error} retry={() => void q.refetch()} />}
      {f && (
        <>
          <Header f={f} can={can} onAction={setDialog} />
          <Segmented value={tab} options={tabs} onChange={setTab} ariaLabel="Staff file" />
          {tab === "overview" && <Overview f={f} can={can} rules={rules} records={records} go={setTab} />}
          {tab === "attendance" && <Attendance f={f} can={can.write} />}
          {tab === "leave" && (
            <Leave f={f} records={records} rules={rules} can={can} onRequest={() => setDialog("leave")} />
          )}
          {tab === "dates" && <Dates e={f.employee} />}
          {tab === "reviews" && <Reviews records={records} onAdd={can.write ? () => setDialog("review") : undefined} />}
          {tab === "discipline" && (
            <Discipline records={records} can={can} onAdd={can.write ? () => setDialog("discipline") : undefined} />
          )}
          {tab === "letters" && (
            <Letters
              records={records}
              can={can}
              onOpen={setLetter}
              onAdd={can.write ? () => setDialog("letter") : undefined}
            />
          )}
          {tab === "documents" && <Documents records={records} onAdd={can.write ? (k) => setDialog(k) : undefined} />}
          {tab === "messages" && <PersonMessages id={f.employee.id} />}
          {f.confidentialHidden && (
            <p className={s.note}>
              {t("Reviews and disciplinary actions need the confidential permission.")}
            </p>
          )}
          {dialog === "edit" && <EmployeeDialog employee={f.employee} onClose={() => setDialog(null)} />}
          {dialog && dialog !== "edit" && (
            <RecordDialog employee={f.employee} kind={dialog} onClose={() => setDialog(null)} />
          )}
          {letter && (
            <LetterView
              record={letter}
              e={f.employee}
              org={session.tenant ?? t("The airport")}
              canApprove={can.approve}
              onClose={() => setLetter(null)}
            />
          )}
        </>
      )}
    </Page>
  );
}

type Can = { pay: boolean; confidential: boolean; write: boolean; approve: boolean };

function Header({ f, can, onAction }: { f: HrStaffFile; can: Can; onAction: (k: RecordKind | "edit") => void }) {
  const e = f.employee;
  const input = useRef<HTMLInputElement>(null);
  const photo = useHrSetPhoto(e.id);
  const [err, setErr] = useState<string | null>(null);
  const today = f.attendance.find((a) => a.day === todayIso());
  return (
    <section className={h.fileHead} data-testid="hr-file-head">
      <div className={h.photoWrap}>
        <Avatar name={e.name} photo={f.photo} size={104} />
        {can.write && (
          <>
            <button
              type="button"
              className={h.photoBtn}
              title={t("Change the photo")}
              aria-label={t("Change the photo")}
              onClick={() => input.current?.click()}
              data-testid="hr-photo-btn"
            >
              📷
            </button>
            <input
              ref={input}
              type="file"
              accept="image/*"
              hidden
              data-testid="hr-photo-input"
              onChange={(ev) => {
                const file = ev.target.files?.[0];
                if (!file) return;
                setErr(null);
                shrinkPhoto(file)
                  .then((url) =>
                    photo.mutate(url, { onError: (x) => setErr(x instanceof Error ? x.message : String(x)) }),
                  )
                  .catch((x) => setErr(x instanceof Error ? x.message : String(x)));
              }}
            />
          </>
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <h2 className={h.fileName}>{e.name}</h2>
        <div className={s.dim}>{[e.jobTitle, e.department, e.employeeNo].filter(Boolean).join(" · ")}</div>
        <div className={h.fileMeta}>
          <Chip colour={e.status === "left" ? "var(--dim)" : e.status === "on_leave" ? "var(--blue)" : "var(--green)"}>
            {t(STATUS_LABEL[e.status])}
          </Chip>
          <Chip>
            {e.saudi ? t("Saudi") : t("Non-Saudi")} · {e.nationality}
          </Chip>
          <Chip>{e.employment === "outsourced" ? t("Outsourced") : t("Direct employee")}</Chip>
          <Chip>{t("{y} years' service", { y: e.serviceYears.toFixed(1) })}</Chip>
          {today && (
            <Chip colour={ATTENDANCE_BY.get(today.status)?.colour}>
              {t("Today: {s}", { s: t(ATTENDANCE_BY.get(today.status)?.label ?? today.status) })}
            </Chip>
          )}
        </div>
        {err && <p className={s.err}>{err}</p>}
      </div>
      <div className={`${h.fileActions} ${h.noPrint}`}>
        {can.write && (
          <>
            <Button variant="primary" onClick={() => onAction("edit")} data-testid="hr-file-edit">
              ✎ {t("Edit details")}
            </Button>
            <Button onClick={() => onAction("leave")} data-testid="hr-file-leave">
              🌴 {t("Request leave")}
            </Button>
            <Button onClick={() => onAction("letter")} data-testid="hr-file-letter">
              ✉ {t("Letter")}
            </Button>
            {can.confidential && <Button onClick={() => onAction("review")}>★ {t("Review")}</Button>}
          </>
        )}
      </div>
    </section>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt>{t(label)}</dt>
      <dd>{children ?? "—"}</dd>
    </>
  );
}

function Overview({
  f,
  can,
  rules,
  records,
  go,
}: {
  f: HrStaffFile;
  can: Can;
  rules: HrRules;
  records: HrRecord[];
  go: (t: Tab) => void;
}) {
  const e = f.employee;
  const v = vacationOf(e, records, rules, new Date());
  const sick = sickLeave(records, new Date());
  const month = f.attendance.filter((a) => a.day >= new Date(Date.now() - 30 * 86_400_000).toISOString().slice(0, 10));
  const att = attendanceSummary(month);
  const next = datesOf(e, new Date()).filter((x) => x.daysAway >= 0)[0];
  const waiting = records.filter((r) => r.status === "requested").length;
  const showHourly = useShowHourly();
  const age = e.dateOfBirth ? Math.floor((Date.now() - Date.parse(e.dateOfBirth)) / (365.25 * 86_400_000)) : null;
  return (
    <>
      <div className={h.stats}>
        <button
          type="button"
          className={h.stat}
          style={{ ["--tint" as string]: "var(--blue)" }}
          onClick={() => go("leave")}
        >
          <span className={h.statN}>{e.employment === "direct" ? Math.floor(v.balance) : "—"}</span>
          <span className={h.statLabel}>{t("days of annual leave left")}</span>
        </button>
        <button
          type="button"
          className={h.stat}
          style={{ ["--tint" as string]: "var(--green)" }}
          onClick={() => go("attendance")}
        >
          <span className={h.statN}>{att.rate == null ? "—" : `${att.rate}%`}</span>
          <span className={h.statLabel}>{t("attendance, last 30 days")}</span>
        </button>
        <button
          type="button"
          className={h.stat}
          style={{ ["--tint" as string]: "#b07ce8" }}
          onClick={() => go("leave")}
        >
          <span className={h.statN}>{sick.usedDays}</span>
          <span className={h.statLabel}>{t("sick days in 12 months")}</span>
        </button>
        <button
          type="button"
          className={h.stat}
          style={{ ["--tint" as string]: "var(--amber)" }}
          onClick={() => go("letters")}
        >
          <span className={h.statN}>{waiting}</span>
          <span className={h.statLabel}>{t("requests waiting")}</span>
        </button>
        {next && (
          <button
            type="button"
            className={h.stat}
            style={{ ["--tint" as string]: "#3cc4c4" }}
            onClick={() => go("dates")}
          >
            <span className={h.statN}>{next.daysAway}</span>
            <span className={h.statLabel}>{t("days to: {what}", { what: t(DATE_LABEL[next.kind]) })}</span>
          </button>
        )}
      </div>
      <div className={h.grid2}>
        <Card title={t("Personal details")}>
          <dl className={h.facts}>
            <Fact label="Date of birth">
              {e.dateOfBirth
                ? `${niceDate(e.dateOfBirth)}${age != null ? ` (${t("{n} years", { n: age })})` : ""}`
                : null}
            </Fact>
            <Fact label="Gender">{e.gender ? t(e.gender === "male" ? "Male" : "Female") : null}</Fact>
            <Fact label="Marital status">
              {e.maritalStatus ? t(e.maritalStatus[0].toUpperCase() + e.maritalStatus.slice(1)) : null}
            </Fact>
            <Fact label="Nationality">{e.nationality}</Fact>
            <Fact label={e.saudi ? "National ID" : "Iqama"}>
              {e.idNumber
                ? `${e.idNumber}${e.idExpiry ? ` · ${t("expires {d}", { d: niceDate(e.idExpiry) })}` : ""}`
                : null}
            </Fact>
            {!e.saudi && (
              <Fact label="Passport">
                {e.passportNo
                  ? `${e.passportNo}${e.passportExpiry ? ` · ${t("expires {d}", { d: niceDate(e.passportExpiry) })}` : ""}`
                  : null}
              </Fact>
            )}
            <Fact label="Phone">{e.phone ? <a href={`tel:${e.phone}`}>{e.phone}</a> : null}</Fact>
            <Fact label="Email">{e.email ? <a href={`mailto:${e.email}`}>{e.email}</a> : null}</Fact>
            <Fact label="Address">{e.address}</Fact>
            <Fact label="Emergency contact">
              {e.emergencyContact?.name
                ? `${e.emergencyContact.name}${e.emergencyContact.relation ? ` (${e.emergencyContact.relation})` : ""}${e.emergencyContact.phone ? ` · ${e.emergencyContact.phone}` : ""}`
                : null}
            </Fact>
          </dl>
        </Card>
        <Card title={t("Work and contract")}>
          <dl className={h.facts}>
            <Fact label="Employee number">{e.employeeNo}</Fact>
            <Fact label="Department">{e.department}</Fact>
            <Fact label="Trade">{tradeLabel(e.trade)}</Fact>
            <Fact label="Job title">{e.jobTitle}</Fact>
            <Fact label="Grade">{e.grade}</Fact>
            <Fact label="Hired on">{niceDate(e.hiredOn)}</Fact>
            <Fact label="Contract">
              {t(e.contractType === "indefinite" ? "Indefinite" : "Fixed term")}
              {e.contractEnd ? ` · ${t("ends {d}", { d: niceDate(e.contractEnd) })}` : ""}
            </Fact>
            <Fact label="Hours a week">{e.weeklyHours}</Fact>
            <Fact label="Cost center">{e.costCentre}</Fact>
            <Fact label="Platform user">{e.principalName}</Fact>
          </dl>
        </Card>
        {can.pay && f.cost ? (
          <Card title={t("Pay and cost")} sub={t("Pay, and cost to the airport.")}>
            <dl className={h.facts}>
              {e.employment === "outsourced" ? (
                <Fact label="Contract charge">{sar(e.outsourcedMonthly)}</Fact>
              ) : (
                <>
                  <Fact label="Basic">{sar(e.basic)}</Fact>
                  <Fact label="Housing">{sar(e.housing)}</Fact>
                  <Fact label="Transport">{sar(e.transport)}</Fact>
                  {e.other ? <Fact label="Other allowances">{sar(e.other)}</Fact> : null}
                  <Fact label="Gross a month">
                    <b>{sar(grossPay(e))}</b>
                  </Fact>
                  <Fact label="GOSI (employer)">
                    {sar(f.cost.gosiEmployer)} ({f.cost.gosiEmployerPct}%, {t(f.cost.gosiRegime ?? "—")})
                  </Fact>
                </>
              )}
              <Fact label="Cost to the airport, a month">
                <b>{sar(f.cost.monthlyCost)}</b>
              </Fact>
              {showHourly && <Fact label="An hour of work">{rate(f.cost.hourlyRate)}</Fact>}
              <Fact label="End of service owed today">{sar(f.cost.eosbLiability)}</Fact>
            </dl>
            <p className={s.note}>
              <Link to="/hr-calculators">{t("Open the calculators")}</Link>
            </p>
          </Card>
        ) : (
          <Card title={t("Pay and cost")}>
            <EmptyState>{t("Pay is shown only to those with the pay permission.")}</EmptyState>
          </Card>
        )}
        <Card title={t("Latest in the file")}>
          {records.length === 0 ? (
            <EmptyState>{t("Nothing recorded yet.")}</EmptyState>
          ) : (
            <ul className={h.list}>
              {records.slice(0, 6).map((r) => (
                <li key={r.id} className={`${h.item} ${h.itemClick}`} onClick={() => go(tabOfKind(r.kind))}>
                  <span className={h.when}>{niceDate(r.occurredOn)}</span>
                  <span className={h.itemMain}>
                    <b>{t(r.title)}</b>
                    <span>{t(KIND_WORD[r.kind])}</span>
                  </span>
                  <StatusChip status={r.status} />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}

const KIND_WORD: Record<RecordKind, string> = {
  leave: "Leave",
  review: "Performance review",
  discipline: "Disciplinary action",
  letter: "Letter",
  document: "Document",
  note: "Note",
  request: "Request",
};
export { KIND_WORD };

export function tabOfKind(k: RecordKind): Tab {
  return (
    {
      leave: "leave",
      review: "reviews",
      discipline: "discipline",
      letter: "letters",
      document: "documents",
      note: "documents",
      request: "letters",
    } as const
  )[k];
}

function Attendance({ f, can }: { f: HrStaffFile; can: boolean }) {
  const set = useHrSetAttendance();
  const today = todayIso();
  const byDay = new Map(f.attendance.map((a) => [a.day, a]));
  /* six weeks, Sunday first, ending this week */
  const end = new Date(`${today}T00:00:00Z`);
  const start = new Date(end.getTime() - (35 + end.getUTCDay()) * 86_400_000);
  const cells: string[] = [];
  for (let d = start.getTime(); d <= end.getTime(); d += 86_400_000) cells.push(new Date(d).toISOString().slice(0, 10));
  const mine = f.attendance.filter((a) => a.day >= cells[0]);
  const sum = attendanceSummary(mine);
  const mark = (st: AttendanceStatus) =>
    set.mutate({
      employeeIds: [f.employee.id],
      day: today,
      status: st,
      checkIn: st === "present" ? new Date().toTimeString().slice(0, 5) : null,
    });
  const current = byDay.get(today)?.status;
  return (
    <div className={h.grid2}>
      <div style={{ gridColumn: "1 / -1" }}>
        <ClockPolicy e={f.employee} canEdit={can} />
      </div>
      <PersonCheckIns id={f.employee.id} />
      <Card title={t("The last six weeks")} sub={t("Each square is a day; hover to see it.")}>
        <div className={h.cal}>
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className={h.calHead}>
              {t(d)}
            </div>
          ))}
          {cells.map((d) => {
            const a = byDay.get(d);
            const def = a ? ATTENDANCE_BY.get(a.status) : undefined;
            return (
              <div
                key={d}
                className={h.calDay}
                style={{ ["--c" as string]: def?.colour }}
                title={`${niceDate(d)} — ${a ? t(def?.label ?? a.status) : t("Not recorded")}${a?.checkIn ? ` · ${a.checkIn}` : ""}${a?.minutesLate ? ` · ${t("{n} min late", { n: a.minutesLate })}` : ""}`}
              >
                {Number(d.slice(8))}
              </div>
            );
          })}
        </div>
        <div className={h.legend}>
          {ATTENDANCE.map((a) => (
            <Chip key={a.key} colour={a.colour}>
              {t(a.label)}
            </Chip>
          ))}
        </div>
      </Card>
      <Card title={t("Today")} sub={niceDate(today)}>
        {can ? (
          <div className={h.statusBtns} data-testid="hr-file-today">
            {ATTENDANCE.slice(0, 6).map((a) => (
              <button
                key={a.key}
                type="button"
                className={h.statusBtn}
                style={{ ["--c" as string]: a.colour }}
                aria-pressed={current === a.key}
                onClick={() => mark(a.key)}
              >
                {t(a.label)}
              </button>
            ))}
          </div>
        ) : (
          <p className={s.note}>{current ? t(ATTENDANCE_BY.get(current)?.label ?? current) : t("Not recorded")}</p>
        )}
        <dl className={h.facts} style={{ marginTop: 14 }}>
          <Fact label="Present">{sum.counts.present + sum.counts.remote}</Fact>
          <Fact label="Late">{`${sum.counts.late} (${t("{n} min", { n: sum.lateMinutes })})`}</Fact>
          <Fact label="Absent">{sum.counts.absent}</Fact>
          <Fact label="On leave">{sum.counts.leave}</Fact>
          <Fact label="Sick">{sum.counts.sick}</Fact>
          <Fact label="Attendance rate">{sum.rate == null ? "—" : `${sum.rate}%`}</Fact>
        </dl>
      </Card>
      <Card title={t("Day by day")} style={{ gridColumn: "1 / -1" }}>
        {f.attendance.length === 0 ? (
          <EmptyState>{t("No attendance recorded yet.")}</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t("Date")}</th>
                <th>{t("Status")}</th>
                <th>{t("In")}</th>
                <th>{t("Out")}</th>
                <th className={s.num}>{t("Minutes late")}</th>
              </tr>
            </thead>
            <tbody>
              {f.attendance.slice(0, 60).map((a) => (
                <tr key={a.day}>
                  <td className={s.mono}>{niceDate(a.day)}</td>
                  <td>
                    <Chip colour={ATTENDANCE_BY.get(a.status)?.colour}>
                      {t(ATTENDANCE_BY.get(a.status)?.label ?? a.status)}
                    </Chip>
                  </td>
                  <td className={s.mono}>{a.checkIn ?? "—"}</td>
                  <td className={s.mono}>{a.checkOut ?? "—"}</td>
                  <td className={s.num}>{a.minutesLate || ""}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function Decide({ r, can }: { r: HrRecord; can: Can }) {
  const d = useHrDecide();
  if (r.status !== "requested" || !can.approve) return null;
  return (
    <span className={h.statusBtns}>
      <Button
        size="sm"
        variant="primary"
        disabled={d.isPending}
        onClick={() => d.mutate({ id: r.id, status: r.kind === "letter" ? "issued" : "approved" })}
        data-testid="hr-approve"
      >
        {r.kind === "letter" ? t("Issue") : t("Approve")}
      </Button>
      <Button size="sm" disabled={d.isPending} onClick={() => d.mutate({ id: r.id, status: "rejected" })}>
        {t("Reject")}
      </Button>
    </span>
  );
}

function Leave({
  f,
  records,
  rules,
  can,
  onRequest,
}: {
  f: HrStaffFile;
  records: HrRecord[];
  rules: HrRules;
  can: Can;
  onRequest: () => void;
}) {
  const e = f.employee;
  const v = vacationOf(e, records, rules, new Date());
  const sick = sickLeave(records, new Date());
  const leave = records.filter((r) => r.kind === "leave");
  return (
    <>
      <div className={h.grid2}>
        <Card
          title={t("Annual leave")}
          sub={t("Art. 109: {a} days a year, {b} after {y} years.", {
            a: rules.leave.days,
            b: rules.leave.daysAfter,
            y: rules.leave.afterYears,
          })}
        >
          {e.employment === "outsourced" ? (
            <EmptyState>{t("An outsourced head's leave is the contractor's.")}</EmptyState>
          ) : (
            <>
              <div className={h.big} data-testid="hr-leave-balance">
                {t("{n} days left", { n: Math.floor(v.balance) })}
              </div>
              <dl className={h.facts}>
                <Fact label="Earned since hiring">{v.accrued}</Fact>
                <Fact label="Taken">{v.taken}</Fact>
                <Fact label="Asked for, not yet decided">{v.pending}</Fact>
                <Fact label="Earning now">{t("{n} days a year", { n: v.perYearNow })}</Fact>
                {can.pay && <Fact label="Worth if paid out (art. 111)">{sar(v.value)}</Fact>}
              </dl>
            </>
          )}
        </Card>
        <Card
          title={t("Sick leave")}
          sub={t("Art. 117, per 12 months: 30 days full pay, 60 at ¾ pay, 30 unpaid.")}
        >
          <div className={h.bar} aria-label={t("Sick leave")}>
            <i style={{ width: `${(Math.min(sick.usedDays, 30) / 120) * 100}%`, background: "var(--green)" }} />
            <i
              style={{
                width: `${(Math.min(Math.max(0, sick.usedDays - 30), 60) / 120) * 100}%`,
                background: "var(--amber)",
              }}
            />
            <i
              style={{
                width: `${(Math.min(Math.max(0, sick.usedDays - 90), 30) / 120) * 100}%`,
                background: "var(--red)",
              }}
            />
          </div>
          <dl className={h.facts} style={{ marginTop: 10 }}>
            <Fact label="Used in 12 months">{t("{n} days", { n: sick.usedDays })}</Fact>
            <Fact label="Left at full pay">{sick.fullPayLeft}</Fact>
            <Fact label="Left at three quarters">{sick.threeQuarterLeft}</Fact>
            <Fact label="Left unpaid">{sick.unpaidLeft}</Fact>
            <Fact label="The next sick day is paid">{t(sick.nextDayPay)}</Fact>
          </dl>
        </Card>
      </div>
      <Card
        title={t("Leave history")}
        actions={
          can.write ? (
            <Button variant="primary" onClick={onRequest}>
              🌴 {t("Request leave")}
            </Button>
          ) : undefined
        }
      >
        {leave.length === 0 ? (
          <EmptyState>{t("No leave recorded yet.")}</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t("Type")}</th>
                <th>{t("From")}</th>
                <th>{t("To")}</th>
                <th className={s.num}>{t("Days")}</th>
                <th>{t("Status")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {leave.map((r) => (
                <tr key={r.id} data-testid="hr-leave-row">
                  <td>{t(LEAVE_BY.get(leaveType(r))?.label ?? r.title)}</td>
                  <td className={s.mono}>{niceDate(r.startsOn)}</td>
                  <td className={s.mono}>{niceDate(r.endsOn)}</td>
                  <td className={s.num}>{leaveDays(r)}</td>
                  <td>
                    <StatusChip status={r.status} />
                  </td>
                  <td>
                    <Decide r={r} can={can} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

function Dates({ e }: { e: HrEmployee }) {
  const all = datesOf(e, new Date());
  return (
    <Card title={t("Dates to remember")} sub={t("Birthdays, anniversaries and what expires, soonest first.")}>
      {all.length === 0 ? (
        <EmptyState>{t("Add birth date and ID expiry in Edit details to see them here.")}</EmptyState>
      ) : (
        <ul className={h.list}>
          {all.map((x) => (
            <li key={x.kind} className={h.item}>
              <span style={{ fontSize: 20 }} aria-hidden="true">
                {
                  {
                    birthday: "🎂",
                    work: "🎉",
                    id_expiry: "🪪",
                    passport_expiry: "🛂",
                    contract_end: "📄",
                    probation_end: "⏳",
                    retirement: "🏁",
                  }[x.kind]
                }
              </span>
              <span className={h.itemMain}>
                <b>
                  {t(DATE_LABEL[x.kind])}
                  {x.years != null
                    ? ` — ${x.kind === "birthday" ? t("turns {n}", { n: x.years }) : t("{n} years", { n: x.years })}`
                    : ""}
                </b>
                <span>{niceDate(x.date)}</span>
              </span>
              <Chip colour={x.daysAway < 0 ? "var(--red)" : x.daysAway <= 30 ? "var(--amber)" : undefined}>
                {x.daysAway < 0
                  ? t("{n} days ago", { n: -x.daysAway })
                  : x.daysAway === 0
                    ? t("Today")
                    : t("in {n} days", { n: x.daysAway })}
              </Chip>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Reviews({ records, onAdd }: { records: HrRecord[]; onAdd?: () => void }) {
  const list = records.filter((r) => r.kind === "review");
  return (
    <Card
      title={t("Performance reviews")}
      sub={t("Confidential.")}
      actions={
        onAdd ? (
          <Button variant="primary" onClick={onAdd}>
            ★ {t("Add a review")}
          </Button>
        ) : undefined
      }
    >
      {list.length === 0 ? (
        <EmptyState>{t("No review recorded yet.")}</EmptyState>
      ) : (
        <ul className={h.list}>
          {list.map((r) => (
            <li key={r.id} className={h.item}>
              <span style={{ color: "var(--amber)", whiteSpace: "nowrap" }} aria-label={String(r.data.rating ?? "")}>
                {"★".repeat(Number(r.data.score ?? 0))}
                <span className={s.dim}>{"☆".repeat(5 - Number(r.data.score ?? 0))}</span>
              </span>
              <span className={h.itemMain}>
                <b>
                  {t(r.title)} — {t(String(r.data.rating ?? ""))}
                </b>
                <span>
                  {[
                    r.data.strengths && `${t("Strengths")}: ${String(r.data.strengths)}`,
                    r.data.goals && `${t("Goals")}: ${String(r.data.goals)}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              <span className={h.when}>{niceDate(r.occurredOn)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Discipline({ records, can, onAdd }: { records: HrRecord[]; can: Can; onAdd?: () => void }) {
  const d = useHrDecide();
  const list = records.filter((r) => r.kind === "discipline");
  return (
    <Card
      title={t("Disciplinary actions")}
      sub={t("Confidential.")}
      actions={onAdd ? <Button onClick={onAdd}>{t("Record an action")}</Button> : undefined}
    >
      {list.length === 0 ? (
        <EmptyState>{t("A clean record: no disciplinary action.")}</EmptyState>
      ) : (
        <ul className={h.list}>
          {list.map((r) => (
            <li key={r.id} className={h.item}>
              <span className={h.when}>{niceDate(r.occurredOn)}</span>
              <span className={h.itemMain}>
                <b>{t(r.title)}</b>
                <span>
                  {t(String(r.data.action ?? ""))}
                  {r.data.article ? ` · ${String(r.data.article)}` : ""}
                </span>
              </span>
              <StatusChip status={r.status} />
              {r.status === "open" && can.write && (
                <Button size="sm" onClick={() => d.mutate({ id: r.id, status: "closed" })}>
                  {t("Close")}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Letters({
  records,
  can,
  onOpen,
  onAdd,
}: {
  records: HrRecord[];
  can: Can;
  onOpen: (r: HrRecord) => void;
  onAdd?: () => void;
}) {
  const list = records.filter((r) => r.kind === "letter" || r.kind === "request");
  return (
    <Card
      title={t("Letters & requests")}
      sub={t("Click a letter to read, print or save it.")}
      actions={
        onAdd ? (
          <Button variant="primary" onClick={onAdd}>
            ✉ {t("New letter")}
          </Button>
        ) : undefined
      }
    >
      {list.length === 0 ? (
        <EmptyState>
          {t("No letters yet. Use New letter for verification, salary, NOC, or bank letters.")}
        </EmptyState>
      ) : (
        <ul className={h.list}>
          {list.map((r) => (
            <li
              key={r.id}
              className={`${h.item} ${r.kind === "letter" ? h.itemClick : ""}`}
              onClick={() => r.kind === "letter" && onOpen(r)}
              data-testid="hr-letter-row"
            >
              <span style={{ fontSize: 20 }} aria-hidden="true">
                {r.kind === "letter" ? "✉" : "📝"}
              </span>
              <span className={h.itemMain}>
                <b>
                  {r.kind === "letter"
                    ? t(LETTER_BY.get(String(r.data.letter_type ?? ""))?.label ?? r.title)
                    : t(r.title)}
                </b>
                <span>
                  {r.kind === "request"
                    ? `${t(REQUEST_BY.get(String(r.data.type ?? ""))?.label ?? "Request")}${r.data.details ? ` · ${String(r.data.details)}` : ""}${r.decisionNote ? ` · ${t("HR")}: ${r.decisionNote}` : ""}`
                    : `${r.reference ?? t("no reference yet")} · ${niceDate(r.occurredOn)}`}
                </span>
              </span>
              <StatusChip status={r.status} />
              <span onClick={(ev) => ev.stopPropagation()}>
                <Decide r={r} can={can} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Documents({ records, onAdd }: { records: HrRecord[]; onAdd?: (k: RecordKind) => void }) {
  const docs = records.filter((r) => r.kind === "document" || r.kind === "note");
  const open = (r: HrRecord) => {
    const data = r.data.file_data;
    if (typeof data === "string" && data.startsWith("data:")) {
      const a = document.createElement("a");
      a.href = data;
      a.download = String(r.data.file ?? r.title);
      a.click();
    }
  };
  return (
    <Card
      title={t("Documents & notes")}
      actions={
        onAdd ? (
          <span className={h.statusBtns}>
            <Button onClick={() => onAdd("document")}>📎 {t("Add a document")}</Button>
            <Button onClick={() => onAdd("note")}>🗒 {t("Add a note")}</Button>
          </span>
        ) : undefined
      }
    >
      {docs.length === 0 ? (
        <EmptyState>{t("No document yet.")}</EmptyState>
      ) : (
        <ul className={h.list}>
          {docs.map((r) => {
            const has = typeof r.data.file_data === "string";
            return (
              <li key={r.id} className={`${h.item} ${has ? h.itemClick : ""}`} onClick={() => has && open(r)}>
                <span style={{ fontSize: 20 }} aria-hidden="true">
                  {r.kind === "note" ? "🗒" : "📄"}
                </span>
                <span className={h.itemMain}>
                  <b>{t(r.title)}</b>
                  <span>
                    {r.kind === "note"
                      ? String(r.data.text ?? "")
                      : has
                        ? t("Click to open {file}", { file: String(r.data.file ?? "") })
                        : String(r.data.file ?? t("kept on paper"))}
                  </span>
                </span>
                <span className={h.when}>{niceDate(r.occurredOn)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function LetterView({
  record,
  e,
  org,
  canApprove,
  onClose,
}: {
  record: HrRecord;
  e: HrEmployee;
  org: string;
  canApprove: boolean;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const d = useHrDecide();
  const type = String(record.data.letter_type ?? "employment");
  const l = letterText(type, e, org, {
    addressee: record.data.addressee as string | undefined,
    purpose: record.data.purpose as string | undefined,
  });
  const date = niceDate(record.decidedAt ?? record.occurredOn);
  const html = `<div class="head"><span>${escapeHtml(org)}</span><span>${escapeHtml(record.reference ?? "DRAFT")} · ${escapeHtml(date)}</span></div><h1>${escapeHtml(l.title)}</h1>${l.paragraphs.map((p) => `<p>${escapeHtml(p)}</p>`).join("")}<p style="margin-top:48px">Human Resources<br>${escapeHtml(org)}</p>`;
  return (
    <dialog
      ref={(el) => {
        ref.current = el;
        if (el && !el.open && typeof el.showModal === "function") el.showModal();
      }}
      onClose={onClose}
      className={s.dialog}
      data-testid="hr-letter-view"
    >
      <div className={s.dialogForm}>
        <div className={h.letter}>
          <div className={h.letterHead}>
            <span>{org}</span>
            <span>
              {record.reference ?? t("Draft — not issued yet")} · {date}
            </span>
          </div>
          <h3>{l.title}</h3>
          {l.paragraphs.map((p, i) => (
            <p key={i} style={{ margin: 0 }}>
              {p}
            </p>
          ))}
          <div className={h.letterSign}>
            <span>Human Resources</span>
            <span>{org}</span>
          </div>
        </div>
        <p className={s.note}>
          {t("Letters are in English, as banks and embassies expect. Print on letterhead and sign.")}
        </p>
        <div className={s.formFoot}>
          <Button variant="primary" onClick={() => void printHtml(l.title, html)} data-testid="hr-letter-print">
            🖨 {t("Print")}
          </Button>
          <Button onClick={() => void downloadHtml(`${l.title} ${e.name}`, html)}>⬇ {t("Save a copy")}</Button>
          {record.status === "requested" && canApprove && (
            <Button onClick={() => d.mutate({ id: record.id, status: "issued" }, { onSuccess: onClose })}>
              {t("Issue it")}
            </Button>
          )}
          <Button onClick={onClose}>{t("Close")}</Button>
        </div>
      </div>
    </dialog>
  );
}

function PersonCheckIns({ id }: { id: string }) {
  const q = useHrClockEvents(todayIso(), id);
  return (
    <Card title={t("Check-ins today")}>
      {!q.data?.length ? (
        <EmptyState>{t("No check-in today yet.")}</EmptyState>
      ) : (
        <ul className={h.list}>
          {q.data.map((e) => (
            <li key={e.id} className={h.item}>
              <span style={{ fontSize: 18 }}>{METHOD[e.method]?.icon}</span>
              <span className={h.itemMain}>
                <b>
                  {e.direction === "in" ? t("In") : t("Out")} ·{" "}
                  {new Date(e.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </b>
                <span>
                  {t(METHOD[e.method]?.label ?? e.method)}
                  {e.place ? ` · ${e.place}` : ""}
                  {e.note ? ` · ${e.note}` : ""}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function PersonMessages({ id }: { id: string }) {
  const q = useHrNotifications({ employeeId: id });
  return (
    <Card
      title={t("Messages sent to this person")}
      sub={t("Sent by SMS and email to the contacts on file.")}
    >
      {q.isLoading ? <Skeleton rows={3} /> : <MessageList items={q.data?.notifications ?? []} showPerson={false} />}
    </Card>
  );
}
